/**
 * Campaign tracking utility functions
 */

/**
 * Additional UTM and tracking parameters to capture
 */
export const ADDITIONAL_TRACKING_PARAMS = [
  // LinkedIn parameters
  'li_fat_id',      // LinkedIn First-party Ad Tracking
  'li_mc',          // LinkedIn Member Campaign
  
  // TikTok parameters  
  'ttclid',         // TikTok Click ID
  
  // Pinterest parameters
  'epik',           // Pinterest Click ID
  
  // Twitter/X parameters
  'twclid',         // Twitter Click ID
  
  // Reddit parameters
  'rdt_cid',        // Reddit Click ID
  
  // Amazon parameters
  'dclid',          // DoubleClick Click ID
  
  // Email parameters
  'mc_cid',         // Mailchimp Campaign ID
  'mc_eid',         // Mailchimp Email ID
  'ml_subscriber',  // Mailerlite Subscriber
  'ml_subscriber_hash', // Mailerlite Hash
  'eid',            // Email ID (generic)
  
  // Affiliate/Partner tracking
  'affiliate_id',
  'partner_id',
  'ref',
  'referral',
  
  // A/B Testing
  'variant',
  'experiment_id',
  'ab_test',
];

/**
 * Get device and browser information
 */
export const getDeviceInfo = () => {
  if (typeof window === 'undefined') return {};
  
  const ua = window.navigator.userAgent;
  const mobile = /Mobile|Android|iPhone|iPad/i.test(ua);
  const tablet = /iPad|Android(?!.*Mobile)/i.test(ua);
  
  return {
    device_type: tablet ? 'tablet' : mobile ? 'mobile' : 'desktop',
    user_agent: ua,
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    language: window.navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
};

/**
 * Get all additional tracking parameters from URL
 */
export const getAdditionalParams = () => {
  if (typeof window === 'undefined') return {};
  
  const params = new URLSearchParams(window.location.search);
  const additional = {};
  
  ADDITIONAL_TRACKING_PARAMS.forEach(param => {
    const value = params.get(param);
    if (value) {
      additional[param] = value;
    }
  });
  
  return additional;
};

/**
 * Format campaign data for form submission
 */
export const addCampaignDataToFields = (baseFields, campaignData) => {
  if (!campaignData || Object.keys(campaignData).length === 0) {
    return baseFields;
  }

  // Map campaign data to form-friendly field names
  const campaignFields = {
    ...baseFields,
    // First touch
    utm_source_first: campaignData.reserved_campaign_source_first,
    utm_campaign_first: campaignData.reserved_campaign_id_first,
    utm_medium_first: campaignData.reserved_campaign_medium_first,
    utm_cid_first: campaignData.reserved_campaign_cid_first,
    utm_content_first: campaignData.reserved_ad_first,
    utm_term_first: campaignData.reserved_adset_first,
    entry_path_first: campaignData.reserved_entry_path_first,
    entry_url_first: campaignData.reserved_entry_url_first,
    
    // Last touch
    utm_source_last: campaignData.reserved_campaign_source_last,
    utm_campaign_last: campaignData.reserved_campaign_id_last,
    utm_medium_last: campaignData.reserved_campaign_medium_last,
    utm_cid_last: campaignData.reserved_campaign_cid_last,
    utm_content_last: campaignData.reserved_ad_last,
    utm_term_last: campaignData.reserved_adset_last,
    entry_path_last: campaignData.reserved_entry_path_last,
    entry_url_last: campaignData.reserved_entry_url_last,
    
    // Calculated/Prioritized
    utm_source_calculated: campaignData.reserved_campaign_source_calculated,
    utm_campaign_calculated: campaignData.reserved_campaign_id_calculated,
    utm_medium_calculated: campaignData.reserved_campaign_medium_calculated,
    utm_cid_calculated: campaignData.reserved_campaign_cid_calculated,
    utm_content_calculated: campaignData.reserved_ad_calculated,
    utm_term_calculated: campaignData.reserved_adset_calculated,
    
    // Session data
    session_count: campaignData.reserved_session_count,
    first_seen: campaignData.reserved_first_seen,
    last_seen: campaignData.reserved_last_seen,
    
    // Device info (captured at submission time)
    ...getDeviceInfo(),
  };

  // Remove undefined values
  Object.keys(campaignFields).forEach(key => {
    if (campaignFields[key] === undefined || campaignFields[key] === '') {
      delete campaignFields[key];
    }
  });

  return campaignFields;
};

/**
 * Check if user is returning visitor
 */
export const isReturningVisitor = (cookieName = 'SingitaSourceTracking') => {
  if (typeof document === 'undefined') return false;
  
  const cookie = document.cookie
    .split(';')
    .find(c => c.trim().startsWith(`${cookieName}=`));
    
  if (!cookie) return false;
  
  try {
    const value = cookie.split('=')[1];
    const decoded = window.atob ? window.atob(value) : value;
    const tracking = JSON.parse(decoded);
    return Array.isArray(tracking) && tracking.length > 1;
  } catch {
    return false;
  }
};

/**
 * Get time since first visit
 */
export const getTimeSinceFirstVisit = (campaignData) => {
  if (!campaignData?.reserved_first_seen) return null;
  
  const firstVisit = new Date(campaignData.reserved_first_seen);
  const now = new Date();
  const diffMs = now - firstVisit;
  
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  
  return { days, hours, totalMs: diffMs };
};